/* NPM */
import { useMemo } from "react";
/* UTILS */
import t from "../../../../../utils/translate";
import {
  DURATION_COUNT,
  OFFER_PLAN_NAME,
  RAMP1_MEDS_SALE_PRICE,
  RAMP2_MEDS_SALE_PRICE,
  SALES_PITCH,
  TOTAL_LABEL
} from "../../../constants";
/* COMPONENTS */
import Column from "../../../../Column";
import Pricing from "../Pricing";
import PricingRow from "../PricingRow";
import Break from "../Break";
import PricingRowLeft from "../PricingRowLeft";
import PricingRowRight from "../PricingRowRight";
import CouponCodeTag from "../PaymentForm/Components/CouponCodeTag";
/* STYLES */
import styles from "./styles.module.scss";

const NewPricing = ({
  clinical,
  plan,
  data,
  handleData,
  setPlan,
  couponCodeDiscount,
  setCouponCodeDiscount,
  isUni3or4Quiz
}) => {
  const {
    hasClinicalAddon,
    label: clinicalLabel,
    retailPrice: clinicalRetailPrice,
    salePrice: clinicalSalePrice,
    showCrossoutPrice: clinicalShowCrossoutPrice,
    sublabel: clinicalSublabel
  } = clinical || {};

  const {
    contentMap,
    formattedRamp2SalePrice,
    formattedRetailPrice,
    formattedSalePrice,
    formattedTotal,
    showCrossoutPrice
  } = plan || {};

  const totalDiscount = useMemo(() => {
    let total = 0;
    const planRetailPrice = parseFloat(formattedRetailPrice?.replace("$", ""));
    const planSalePrice = parseFloat(formattedSalePrice?.replace("$", ""));

    if (planSalePrice < planRetailPrice) {
      total += planRetailPrice;
    }

    if (hasClinicalAddon && clinicalLabel) {
      const salePrice = parseFloat(clinicalSalePrice?.replace("$", ""));
      const retailPrice = parseFloat(clinicalRetailPrice?.replace("$", ""));

      if (salePrice < retailPrice) {
        total += retailPrice;
      }
    }

    return `$${total}`;
  }, [
    clinicalRetailPrice,
    clinicalSalePrice,
    formattedRetailPrice,
    formattedSalePrice,
    hasClinicalAddon,
    clinicalLabel
  ]);

  const {
    ramp1MedsSalesPrice,
    ramp2MedsSalesPrice,
    formattedDurationLabel,
    durationLabel,
    offerPlanName,
    salesPitch,
    totalLabel
  } = useMemo(() => {
    const durationLabel = contentMap?.get(DURATION_COUNT)?.toLowerCase();

    return {
      durationLabel,
      formattedDurationLabel: durationLabel,
      offerPlanName: contentMap?.get(OFFER_PLAN_NAME),
      ramp1MedsSalesPrice: contentMap?.get(RAMP1_MEDS_SALE_PRICE),
      ramp2MedsSalesPrice: contentMap?.get(RAMP2_MEDS_SALE_PRICE),
      salesPitch: contentMap?.get(SALES_PITCH),
      totalLabel: contentMap?.get(TOTAL_LABEL)
    };
  }, [contentMap]);

  return (
    <>
      <h1 className={styles.heading}>{t("CLINIC_CHECKOUT_PAYMENT_HEADER")}</h1>
      <Pricing>
        <PricingRow>
          <PricingRowLeft>
            <p className={styles.subheading}>
              {t("CLINIC_CHECKOUT_PAYMENT_REVIEW")}
            </p>
          </PricingRowLeft>
        </PricingRow>
        <Break />
        <PricingRow>
          {ramp2MedsSalesPrice &&
          parseInt(ramp2MedsSalesPrice, 10) >
            parseInt(ramp1MedsSalesPrice, 10) ? (
            <PricingRowLeft>
              <p className={styles.rowHeading}>{t("YOUR_FIRST_MONTH")}</p>
              <div className={styles.rowText}>
                {offerPlanName}
                <p className={styles.salePrice}>
                  {formattedRamp2SalePrice}
                  <span className={styles.duration}>
                    /{formattedDurationLabel}
                    {t("AFTER_FIRST_DURATION", {
                      duration: durationLabel
                    })}
                  </span>
                </p>
              </div>
            </PricingRowLeft>
          ) : (
            <PricingRowLeft>
              <p className={styles.rowHeading}>
                {t("CLINIC_CHECKOUT_YOU_CHOSE")}
              </p>
              <div className={styles.rowText}>
                {offerPlanName}
                <p className={styles.salePrice}>
                  {formattedSalePrice}
                  <span className={styles.duration}>/{durationLabel}</span>
                </p>
              </div>
            </PricingRowLeft>
          )}
          <PricingRowRight>
            <div className={styles.price}>
              {showCrossoutPrice && (
                <p className={styles.retailPrice}>{formattedRetailPrice}</p>
              )}
              <p> {formattedSalePrice}</p>
            </div>
          </PricingRowRight>
        </PricingRow>
        {hasClinicalAddon && clinicalLabel && (
          <>
            <Break />
            <PricingRow>
              <PricingRowLeft>
                <p className={styles.rowHeading}>{clinicalSublabel}</p>
                <div className={styles.rowText}>{clinicalLabel}</div>
              </PricingRowLeft>
              <PricingRowRight>
                <div className={styles.price}>
                  {clinicalShowCrossoutPrice && (
                    <p className={styles.retailPrice}>{clinicalRetailPrice}</p>
                  )}
                  <p>{clinicalSalePrice}</p>
                </div>
              </PricingRowRight>
            </PricingRow>
          </>
        )}
        {!salesPitch && isUni3or4Quiz && <Break />}
        {data?.couponCode && (
          <>
            <Break />
            <PricingRow>
              <PricingRowLeft>
                <p className={styles.rowHeading}>{t("COUPON_CODE")}</p>
                <CouponCodeTag
                  data={data}
                  onChange={handleData}
                  setPlan={setPlan}
                  setDiscount={setCouponCodeDiscount}
                />
              </PricingRowLeft>
              <PricingRowRight>
                <div className={styles.price}>
                  <p>{`${couponCodeDiscount.discount}`}</p>
                </div>
              </PricingRowRight>
            </PricingRow>
          </>
        )}
        <Column>
          {salesPitch ? (
            <PricingRow className={styles.salesPitchRow}>
              <div
                className={styles.salesPitch}
                dangerouslySetInnerHTML={{
                  __html: salesPitch
                }}
              />
            </PricingRow>
          ) : (
            <Break />
          )}
          <PricingRow>
            <PricingRowLeft>
              <p className={styles.rowHeading}>{totalLabel}</p>
            </PricingRowLeft>
            <PricingRowRight>
              <div className={styles.price}>
                {showCrossoutPrice && totalDiscount && (
                  <p className={styles.retailPrice}>{totalDiscount}</p>
                )}
                <p>{couponCodeDiscount.total || formattedTotal}</p>
              </div>
            </PricingRowRight>
          </PricingRow>
        </Column>
      </Pricing>
    </>
  );
};

export default NewPricing;
